import { Button, Checkbox, Space, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import type { StaticMenuItem } from '../../config/menuConfig';
import { menuLabelKey, pageKeyOf, resolveMenuLabel } from '../../domain/pagePermissions';
import { menuIconFor } from '../menuIcons';

interface PermissionMenuListProps {
    items: readonly StaticMenuItem[];
    /** The page keys currently ticked. */
    selected: readonly string[];
    /** The page key whose preview is showing. */
    previewedKey: string | null;
    disabled: boolean;
    onToggle: (pageKey: string, checked: boolean) => void;
    onPreview: (pageKey: string) => void;
    onSelectAll: () => void;
    onClearAll: () => void;
}

/**
 * The menu as the user would see it, with a checkbox for every page.
 *
 * Ticking decides whether the category may open the page; clicking the page itself only shows it in
 * the preview. The two are separate controls on purpose: looking at a page must never change what
 * is allowed.
 */
const PermissionMenuList = ({
    items,
    selected,
    previewedKey,
    disabled,
    onToggle,
    onPreview,
    onSelectAll,
    onClearAll,
}: PermissionMenuListProps) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();

    return (
        <div data-testid="permission-menu" style={{ width: 280, flex: '0 0 280px' }}>
            <Space style={{ marginBottom: 8 }} wrap>
                <Button size="small" onClick={onSelectAll} disabled={disabled}>
                    {t('configuration.permissions.selectAll')}
                </Button>
                <Button size="small" onClick={onClearAll} disabled={disabled}>
                    {t('configuration.permissions.clearAll')}
                </Button>
            </Space>
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
                {t('configuration.permissions.menuHint')}
            </Typography.Paragraph>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {items.map(item => {
                    const pageKey = pageKeyOf(item.path);
                    const labelKey = menuLabelKey(item.path);
                    const label = resolveMenuLabel(t(labelKey), labelKey, item.name);
                    const isPreviewed = previewedKey === pageKey;

                    return (
                        <li
                            key={pageKey}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '4px 8px',
                                borderRadius: token.borderRadius,
                                background: isPreviewed ? token.colorPrimaryBg : 'transparent',
                            }}
                        >
                            <Checkbox
                                checked={selected.includes(pageKey)}
                                disabled={disabled}
                                onChange={event => onToggle(pageKey, event.target.checked)}
                                aria-label={t('configuration.permissions.allowPage', {
                                    page: label,
                                })}
                                data-testid={`permission-check-${pageKey}`}
                            />
                            <button
                                type="button"
                                onClick={() => onPreview(pageKey)}
                                aria-pressed={isPreviewed}
                                data-testid={`permission-page-${pageKey}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    flex: 1,
                                    border: 0,
                                    background: 'transparent',
                                    color: token.colorText,
                                    cursor: 'pointer',
                                    padding: '4px 0',
                                    textAlign: 'left',
                                    font: 'inherit',
                                }}
                            >
                                <span style={{ display: 'inline-flex', fontSize: 18 }}>
                                    {menuIconFor(pageKey)}
                                </span>
                                {label}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default PermissionMenuList;
